import { GzGeneralChartWebComponent } from '../component/GzGeneralChartWebComponent';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { ApexOptions } from '../../../charts/apexchart';

export const CHART_AREA_OPTIONS: ApexOptions = {
  chart: {
    type: 'area',
    foreColor: '#000524',
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
    events: {
      dataPointMouseEnter: (mouseEvent: MouseEvent, options: any, config: any): void => {
        GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
      },
      mouseMove: (mouseEvent: MouseEvent, options: any, config: any): void => {
        GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
      },
    },
  },
  colors: ['#379FE3'],
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 1,
  },
  plotOptions: {
    bar: {
      distributed: true,
    },
  },
  fill: {
    opacity: 0.6,
    type: 'solid',
    colors: ['#019EE066'],
    gradient: {
      enabled: false,
      opacityFrom: 1,
      opacityTo: 0,
    },
  },
  grid: {
    position: 'back',
    borderColor: 'var(--chart-101-pressed-dividers)',
    clipMarkers: false,
    yaxis: {
      lines: {
        show: true,
      },
    },
    xaxis: {
      lines: {
        show: true,
      },
    },
  },
  tooltip: {
    enabled: true,
    followCursor: true,
    intersect: false,
    custom({ series, seriesIndex, dataPointIndex, w }): string {
      return `<div class="tooltip">
                <span class='tooltip--bullet' style="background-color: ${w.globals.colors[seriesIndex]}"></span>
                <span class='tooltip--label'>${w.globals.seriesNames[seriesIndex]}: </span>
                <span class='tooltip--value'>${series[seriesIndex][dataPointIndex]}</span>
              </div>
          `;
    },
  },
  yaxis: {
    opposite: false,
    tickAmount: 10,
  },
  legend: {
    show: true,
    showForSingleSeries: true,
    position: 'bottom',
    horizontalAlign: 'center',
    floating: false,
    fontSize: '14px',
    fontFamily: 'IBMPlex',
    fontWeight: 400,
    labels: {
      colors: undefined,
      useSeriesColors: false,
    },
    itemMargin: {
      horizontal: 15,
      vertical: 0,
    },
    markers: {
      width: 10,
      height: 10,
      strokeWidth: 0,
      radius: 0,
    },
    onItemHover: {
      highlightDataSeries: true,
    },
    onItemClick: {
      toggleDataSeries: false
    },
    formatter: function (seriesName, opts): string | undefined {
      if (isEmpty(opts.w.globals.series[opts.seriesIndex])) {
        return `${seriesName}`;
      }
      return `${seriesName}: <b>${Math.max(...opts.w.globals.series[opts.seriesIndex])}</b>`;
    },
  },
  states: {
    hover: {
      filter: {
        type: 'lighten',
        value: 0.05,
      },
    },
  },
  noData: {
    text: 'no data',
    align: 'center',
    verticalAlign: 'middle',
    offsetX: 0,
    offsetY: 0,
    style: {
      color: undefined,
      fontSize: '14px',
      fontFamily: undefined,
    },
  },
};

export const CHART_LINE_OPTIONS: ApexOptions = {
  chart: {
    type: 'line',
    toolbar: {
      show: false,
    },
    foreColor: '#000524',
    zoom: {
      enabled: false,
    },
    events: {
      dataPointMouseEnter: (mouseEvent: MouseEvent, options: any, config: any): void => {
        GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
      },
      mouseMove: (mouseEvent: MouseEvent, options: any, config: any): void => {
        GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
      },
    },
  },
  colors: ['#379FE3'],
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 1,
  },
  plotOptions: {
    bar: {
      distributed: true,
    },
  },
  fill: {
    opacity: 0.8,
    type: 'solid',
    colors: ['#019EE066', '#F95402'],
    gradient: {
      enabled: false,
      opacityFrom: 1,
      opacityTo: 0,
    },
  },
  tooltip: {
    enabled: true,
    followCursor: true,
    intersect: false,
    custom({ series, seriesIndex, dataPointIndex, w }): string {
      return `<div class="tooltip">
                <span class='tooltip--bullet' style="background-color: ${w.globals.colors[seriesIndex]}"></span>
                <span class='tooltip--label'>${w.globals.seriesNames[seriesIndex]}: </span>
                <span class='tooltip--value'>${series[seriesIndex][dataPointIndex]}</span>
              </div>
          `;
    },
  },
  grid: {
    position: 'back',
    borderColor: 'var(--chart-101-pressed-dividers)',
    clipMarkers: false,
    yaxis: {
      lines: {
        show: true,
      },
    },
    xaxis: {
      lines: {
        show: true,
      },
    },
  },
  yaxis: {
    opposite: false,
    tickAmount: 10,
  },
  legend: {
    show: true,
    showForSingleSeries: true,
    position: 'bottom',
    horizontalAlign: 'center',
    floating: false,
    fontSize: '14px',
    fontFamily: 'IBMPlex',
    fontWeight: 400,
    labels: {
      colors: undefined,
      useSeriesColors: false,
    },
    itemMargin: {
      horizontal: 5,
      vertical: 0,
    },
    markers: {
      width: 10,
      height: 10,
      strokeWidth: 0,
      radius: 0,
    },
    onItemHover: {
      highlightDataSeries: true,
    },
    onItemClick: {
      toggleDataSeries: false
    },
    formatter: function (seriesName, opts): string | undefined {
      if (isEmpty(opts.w.globals.series[opts.seriesIndex])) {
        return `${seriesName}`;
      }
      return `${seriesName}: <b>${Math.max(...opts.w.globals.series[opts.seriesIndex])}</b>`;
    },
  },
  states: {
    hover: {
      filter: {
        type: 'lighten',
        value: 0.05,
      },
    },
  },
  noData: {
    text: 'no data',
    align: 'center',
    verticalAlign: 'middle',
    offsetX: 0,
    offsetY: 0,
    style: {
      color: undefined,
      fontSize: '14px',
      fontFamily: undefined,
    },
  },
};

export const CHART_RADAR_OPTIONS: ApexOptions = {
  chart: {
    type: 'radar',
    toolbar: {
      show: false,
    },
    foreColor: '#000524',
    events: {
      dataPointMouseEnter: (mouseEvent: MouseEvent, options: any, config: any): void => {
        GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
      },
      mouseMove: (mouseEvent: MouseEvent, options: any, config: any): void => {
        GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
      },
    },
  },
  colors: ['#379FE3'],
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 1,
  },
  plotOptions: {
    bar: {
      distributed: true,
    },
  },
  fill: {
    opacity: 0.3,
    type: 'solid',
    colors: ['#019EE066', '#F95402'],
    gradient: {
      enabled: false,
      opacityFrom: 1,
      opacityTo: 0,
    },
  },
  tooltip: {
    enabled: true,
    custom({ series, seriesIndex, dataPointIndex, w }): string {
      return `<div class="tooltip">
                <span class='tooltip--bullet' style="background-color: ${w.globals.colors[seriesIndex]}"></span>
                <span class='tooltip--label'>${w.globals.seriesNames[seriesIndex]}: </span>
                <span class='tooltip--value'>${series[seriesIndex][dataPointIndex]}</span>
              </div>
          `;
    },
  },
  grid: {
    position: 'back',
    borderColor: 'var(--chart-101-pressed-dividers)',
    clipMarkers: false,
    yaxis: {
      lines: {
        show: true,
      },
    },
    xaxis: {
      lines: {
        show: true,
      },
    },
  },
  yaxis: {
    show: true,
    showAlways: false,
    showForNullSeries: true,
    opposite: false,
    tickAmount: undefined,
    forceNiceScale: false,
    floating: false,
    labels: {
      show: true,
      minWidth: 0,
      maxWidth: 160,
      style: {
        colors: [],
        fontSize: '12px',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontWeight: 400,
        cssClass: 'apexcharts-yaxis-label',
      },
      offsetX: 0,
      offsetY: 0,
    },
    axisBorder: {
      show: true,
      color: '#78909C',
      offsetX: 0,
      offsetY: 0
    },
    axisTicks: {
      show: true,
      borderType: 'solid',
      color: '#78909C',
      width: 6,
      offsetX: 0,
      offsetY: 0
    },
    crosshairs: {
      show: true,
      position: 'back',
      stroke: {
        color: 'var(--chart-101-pressed-dividers)',
        width: 1,
        dashArray: 0,
      },
    }
  },
  legend: {
    show: true,
    showForSingleSeries: true,
    position: 'bottom',
    horizontalAlign: 'center',
    floating: false,
    fontSize: '14px',
    fontFamily: 'IBMPlex',
    fontWeight: 400,
    labels: {
      colors: undefined,
      useSeriesColors: false,
    },
    itemMargin: {
      horizontal: 5,
      vertical: 0,
    },
    markers: {
      width: 10,
      height: 10,
      strokeWidth: 0,
      radius: 0,
    },
    onItemHover: {
      highlightDataSeries: true,
    },
    onItemClick: {
      toggleDataSeries: false
    },
    formatter: function (seriesName, opts): string | undefined {
      if (isEmpty(opts.w.globals.series[opts.seriesIndex])) {
        return `${seriesName}`;
      }
      return `${seriesName}: <b>${Math.max(...opts.w.globals.series[opts.seriesIndex])}</b>`;
    },
  },
  states: {
    hover: {
      filter: {
        type: 'lighten',
        value: 0.05,
      },
    },
  },
  noData: {
    text: 'no data',
    align: 'center',
    verticalAlign: 'middle',
    offsetX: 0,
    offsetY: 0,
    style: {
      color: undefined,
      fontSize: '14px',
      fontFamily: undefined,
    },
  },
};

export const CHART_BAR_OPTIONS: ApexOptions = {
  chart: {
    type: 'bar',
    foreColor: '#000524',
    stackType: '100%',
    stacked: true,
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
    events: {
      dataPointMouseEnter: (mouseEvent: MouseEvent, options: any, config: any): void => {
        GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
      },
      mouseMove: (mouseEvent: MouseEvent, options: any, config: any): void => {
        GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
      },
    },
  },
  theme: {
    mode: 'dark',
  },
  colors: ['#922227', '#D23138', '#EF5350', '#FF883F', '#FFD600'],
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 1,
  },
  plotOptions: {
    bar: {
      distributed: true,
      horizontal: false,
      columnWidth: '70%',
      barHeight: '100%',
    },
  },
  fill: {
    opacity: 0.8,
    type: 'solid',
    colors: ['#922227', '#D23138', '#EF5350', '#FF883F', '#FFD600'],
    gradient: {
      enabled: false,
      opacityFrom: 1,
      opacityTo: 0,
    },
  },
  grid: {
    position: 'back',
    borderColor: 'var(--chart-101-pressed-dividers)',
    clipMarkers: false,
    yaxis: {
      lines: {
        show: true,
      },
    },
    xaxis: {
      lines: {
        show: true,
      },
    },
  },
  yaxis: {
    opposite: false,
    tickAmount: 10,
    crosshairs: {
      show: false,
    },
  },
  legend: {
    show: true,
    showForSingleSeries: false,
    showForZeroSeries: true,
    showForNullSeries: true,
    position: 'bottom',
    horizontalAlign: 'center',
    floating: false,
    fontSize: '14px',
    fontFamily: 'IBMPlex',
    fontWeight: 400,
    labels: {
      colors: ['#1A1A1A'],
      useSeriesColors: false,
    },
    itemMargin: {
      horizontal: 5,
      vertical: 0,
    },
    markers: {
      width: 10,
      height: 10,
      strokeWidth: 0,
      radius: 0,
    },
    onItemHover: {
      highlightDataSeries: true,
    },
    onItemClick: {
      toggleDataSeries: false
    },
    formatter: function (seriesName, opts): string | undefined {
      if (isEmpty(opts.w.globals.series[opts.seriesIndex])) {
        return `${seriesName}`;
      }
      return `${seriesName}: <b>${Math.max(...opts.w.globals.series[opts.seriesIndex])}</b>`;
    },
  },
  tooltip: {
    enabled: true,
    followCursor: true,
    intersect: true,
    custom({ series, seriesIndex, dataPointIndex, w }): string {
      return `<div class="tooltip">
                <span class='tooltip--bullet' style="background-color: ${w.globals.colors[seriesIndex]}"></span>
                <span class='tooltip--label'>${w.globals.seriesNames[seriesIndex]}: </span>
                <span class='tooltip--value'>${series[seriesIndex][dataPointIndex]}</span>
              </div>
          `;
    },
  },
  states: {
    normal: {
      filter: {
        type: 'none',
      },
    },
    hover: {
      filter: {
        type: 'none',
      },
    },
    active: {
      filter: {
        type: 'none',
      },
    },
  },
  noData: {
    text: 'no data',
    align: 'center',
    verticalAlign: 'middle',
    offsetX: 0,
    offsetY: 0,
    style: {
      color: undefined,
      fontSize: '14px',
      fontFamily: undefined,
    },
  },
};

export const CHART_DONUT_OPTIONS: ApexOptions = {
  chart: {
    type: 'donut',
    foreColor: '#000524',
    width: '600px',
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
    events: {
      dataPointMouseEnter: (mouseEvent: MouseEvent, options: any, config: any): void => {
        GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
      },
      mouseMove: (mouseEvent: MouseEvent, options: any, config: any): void => {
        GzGeneralChartWebComponent.alignTooltip(mouseEvent, options, config);
      },
    },
  },
  theme: {
    mode: 'dark',
  },
  colors: ['#922227', '#D23138', '#EF5350', '#FF883F', '#FFD600'],
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 4,
  },
  plotOptions: {
    pie: {
      startAngle: 0,
      endAngle: 360,
      expandOnClick: true,
      offsetX: -20,
      offsetY: 0,
      customScale: 0.8,
      dataLabels: {
        offset: 0,
        minAngleToShowLabel: 10,
      },
      donut: {
        size: '85%',
        background: 'transparent',
        labels: {
          show: true,
          name: {
            show: true,
            fontSize: '14px',
            fontFamily: 'IBMPlex',
            fontWeight: 600,
            color: undefined,
            offsetY: 20,
            formatter: function (val: string): string {
              return val;
            },
          },
          value: {
            show: true,
            fontSize: '24px',
            fontFamily: 'IBMPlex',
            fontWeight: 400,
            color: undefined,
            offsetY: -20,
            formatter: function (val: number): number {
              return val;
            },
          },
          total: {
            show: true,
            showAlways: true,
            label: 'Total',
            fontSize: '22px',
            fontFamily: 'IBMPlex',
            fontWeight: 600,
            color: '#373d3f',
            formatter: function (w: any): number {
              return w.globals.seriesTotals.reduce((a, b) => {
                return a + b;
              }, 0);
            },
          },
        },
      },
    },
  },
  fill: {
    opacity: 0.8,
    type: 'solid',
    colors: ['#922227', '#D23138', '#EF5350', '#FF883F', '#FFD600'],
    gradient: {
      enabled: false,
      opacityFrom: 1,
      opacityTo: 0,
    },
  },
  grid: {
    position: 'back',
    borderColor: 'var(--chart-101-pressed-dividers)',
    clipMarkers: false,
    yaxis: {
      lines: {
        show: true,
      },
    },
    xaxis: {
      lines: {
        show: true,
      },
    },
  },
  yaxis: {
    opposite: false,
    tickAmount: 10,
    crosshairs: {
      show: false,
    },
  },
  legend: {
    show: true,
    showForSingleSeries: false,
    showForZeroSeries: true,
    showForNullSeries: true,
    position: 'right',
    horizontalAlign: 'center',
    floating: false,
    fontSize: '12px',
    fontFamily: 'Montserrat',
    fontWeight: 400,
    labels: {
      colors: ['#1A1A1A'],
      useSeriesColors: false,
    },
    itemMargin: {
      horizontal: 0,
      vertical: 2,
    },
    markers: {
      width: 10,
      height: 10,
      strokeWidth: 0,
      radius: 0,
    },
    onItemHover: {
      highlightDataSeries: true,
    },
    onItemClick: {
      toggleDataSeries: false
    },
    formatter: function (seriesName, opts): string | undefined {
      if (isEmpty(opts.w.globals.series[opts.seriesIndex]) ||  opts.w.globals.series[opts.seriesIndex] === 0) {
        return '-';
      }

      return `<div class="chart-legend">
                <gz-tooltip class="chart-legend-text" tooltipText="${seriesName}" isEllipsis>
                  <gz-text fontSize="12">${seriesName}:</gz-text>
                </gz-tooltip>
                <div class="chart-legend-value">${opts.w.globals.series[opts.seriesIndex]}</div>
              </div>
      `;
    },
  },
  tooltip: {
    enabled: true,
    followCursor: true,
  },
  states: {
    normal: {
      filter: {
        type: 'none',
      },
    },
    hover: {
      filter: {
        type: 'none',
      },
    },
    active: {
      filter: {
        type: 'none',
      },
    },
  },
  noData: {
    text: 'no data',
    align: 'left',
    verticalAlign: 'middle',
    offsetX: 0,
    offsetY: 0,
    style: {
      color: undefined,
      fontSize: '12px',
      fontFamily: undefined,
    },
  },
};
